import React from 'react'
import { FormattedMessage } from 'react-intl'
import { PureQueryOptions } from 'apollo-client'
import { ApplicationImportance } from '../application-importance'
import { theme } from '../../styled'
import { MetadataValue } from '../applications-grid/shared'
import { css } from '@emotion/css'
import { AppUnlicensedLink } from '../link'
import {
  SizedTooltipList,
  Url,
  Importance,
  AttackStatus,
  Metadata,
  Language
} from './tooltip-content-pieces'
import { AgentLanguage } from '../agent-language'
import { AgentFeature, AgentFeaturesLoader } from '../agent-features'
import { Role } from '@contrast-security-inc/base-types'
import { ApplicationTooltipLoader } from './application-tooltip-loader'
import { ViewerLoader } from '../viewer'

export function ProtectApplicationTooltip({
  organizationID,
  applicationID,
  showAttackStatus,
  showLabel
}) {
  return (
    <ViewerLoader fetchPolicy="cache-first">
      {({ viewer, queryInfo: viewerQueryInfo }) => {
        return (
          <AgentFeaturesLoader
            fetchPolicy="cache-first"
            render={({ features }) => {
              if (!viewer) {
                return false
              }

              const {
                currentOrganization: {
                  assessLicenseBreakdown: { unused: unusedAssessLicenses }
                },
                protect
              } = viewer
              return (
                <ApplicationTooltipLoader
                  organizationID={organizationID}
                  applicationID={applicationID}
                >
                  {({ application, queryInfo: applicationQueryInfo }) => {
                    const {
                      license,
                      name,
                      url,
                      importance,
                      attackStatus,
                      hasAProtectedServer,
                      customMetadataFields,
                      role,
                      language
                    } = application
                    return (
                      <ProtectApplicationTooltipContent
                        organizationID={organizationID}
                        organizationRole={role}
                        id={applicationID}
                        name={name}
                        licensed={!!license}
                        importance={importance}
                        showAttackStatus={showAttackStatus}
                        attackStatus={attackStatus}
                        hasAProtectedServer={hasAProtectedServer}
                        protect={protect}
                        metadata={customMetadataFields}
                        refetchQueries={[viewerQueryInfo, applicationQueryInfo]}
                        url={url}
                        features={!!features ? features : []}
                        hasLicenses={unusedAssessLicenses > 0}
                        language={AgentLanguage.fromKey(language)}
                        showLabel={showLabel}
                      />
                    )
                  }}
                </ApplicationTooltipLoader>
              )
            }}
          />
        )
      }}
    </ViewerLoader>
  )
}

export interface ProtectApplicationTooltipContentProps {
  features: AgentFeature[]
  organizationID: string
  organizationRole: Role
  hasLicenses: boolean
  id: string
  name: string
  language: AgentLanguage
  onLicense?: () => void

  licensed: boolean
  url: string
  importance: ApplicationImportance
  attackStatus?: string
  hasAProtectedServer: boolean
  protect: boolean
  metadata?: MetadataValue[]
  refetchQueries?: PureQueryOptions[]
  showAttackStatus?: boolean
  showLabel?: boolean
}

export function ProtectApplicationTooltipContent({
  organizationID,
  organizationRole,
  hasLicenses,
  id,
  name,
  url,
  importance,
  licensed,
  language,
  features,
  attackStatus,
  hasAProtectedServer,
  protect,
  onLicense,
  metadata,
  refetchQueries,
  showAttackStatus = true,
  showLabel = false
}: ProtectApplicationTooltipContentProps) {
  return (
    <SizedTooltipList>
      {showLabel && (
        <FormattedMessage
          id="title_application"
          children={(message) => (
            <li
              className={css`
                font-weight: ${theme.fontWeights.secondary.boldest};
                padding-bottom: 1px;
                margin-bottom: 1px;
              `}
            >
              {message}
            </li>
          )}
        />
      )}
      {!licensed && (
        <li
          className={css`
            margin-bottom: 10px;
          `}
        >
          <AppUnlicensedLink
            features={features}
            organizationID={organizationID}
            role={organizationRole}
            archived={false}
            hasLicenses={hasLicenses}
            applicationID={id}
            applicationName={name}
            applicationLanguage={language}
            refetchQueries={refetchQueries}
            refresh={
              onLicense
                ? () => {
                    onLicense()
                    return Promise.resolve()
                  }
                : undefined
            }
          />
        </li>
      )}
      <li>
        <Url url={url} />
      </li>
      <li>
        <Importance importance={importance} />
      </li>
      <li>
        <Language language={language.getDisplay()} />
      </li>
      {showAttackStatus && protect && (
        <li>
          <AttackStatus
            attackStatus={attackStatus}
            hasAProtectedServer={hasAProtectedServer}
          />
        </li>
      )}
      {metadata && <Metadata values={metadata} />}
    </SizedTooltipList>
  )
}
